import type { RechargeRenderData } from './types';
import {
  FIELD_KEYS,
  SUPPLIER_HIDDEN_FIELDS,
  CHANNEL_L1_CATEGORIES,
  PLATFORM_L1_CATEGORIES,
} from './constants';

/**
 * 充值发起表单字段显隐规则（按 角色 × 入口 × 国内/海外 × 核算预判）。
 * 依据 PRD §4.1.1.4.1 字段规则 + 全站技术方案设计.md §M1。
 */
export function isFieldVisible(field: string, rd: RechargeRenderData): boolean {
  const { ctx, detailData, calc } = rd;
  // 供应商端精简：隐藏预算/定标/执行日期/超充/提前充值相关字段
  if (ctx?.initiator_role === 'SUPPLIER' && SUPPLIER_HIDDEN_FIELDS.has(field)) {
    return false;
  }
  switch (field) {
    case FIELD_KEYS.CHANNEL:
      // 仅效果买量/收口投放/品效推广显示（按一级需求类型 l1_category_id 判断）
      return CHANNEL_L1_CATEGORIES.has(detailData?.l1_category_id);
    case FIELD_KEYS.PLATFORM:
      // 仅喇叭体系显示（按一级需求类型 l1_category_id 判断）
      return PLATFORM_L1_CATEGORIES.has(detailData?.l1_category_id);
    case FIELD_KEYS.QUOTE_PRICE:
      // 仅非绑定执行数据展示
      return detailData?.is_bind_execution_data === false;
    case FIELD_KEYS.OPTIMIZER_IDS:
      // 仅外包端供应商发起显示（策划/优化师发起不选优化师）；且仅国内
      return ctx?.isExternal === true && ctx?.initiator_role === 'SUPPLIER' && !ctx?.overseas;
    case FIELD_KEYS.IS_OVER_CHARGE:
      // 仅 is_early_recharge=否 且策划/优化师发起时显示（只读，策划据此填超充原因；供应商隐藏）
      return calc?.is_early_recharge === false
        && (ctx?.initiator_role === 'PLANNER' || ctx?.initiator_role === 'OPTIMIZER');
    case FIELD_KEYS.OVER_CHARGE_REASON:
      // need_over_charge_reason=true 时显示（仅策划发起，优化师/供应商发起留策划确认节点填）
      return ctx?.initiator_role === 'PLANNER' && calc?.need_over_charge_reason === true;
    case FIELD_KEYS.IS_EARLY_RECHARGE:
      // 策划/优化师发起时只读展示"是/否"（供应商发起不展示核算判断）；need=true 时同时展示原因框
      return ctx?.initiator_role === 'PLANNER' || ctx?.initiator_role === 'OPTIMIZER';
    case FIELD_KEYS.EARLY_RECHARGE_REASON:
      // need_early_recharge_reason=true 时显示（仅策划发起，优化师/供应商发起留策划确认节点填）
      return ctx?.initiator_role === 'PLANNER' && calc?.need_early_recharge_reason === true;
    default:
      return true;
  }
}

/** 充值发起表单字段必填规则 */
export function isFieldRequired(field: string, rd: RechargeRenderData): boolean {
  const { ctx, calc } = rd;
  if (!isFieldVisible(field, rd)) return false;
  switch (field) {
    case FIELD_KEYS.ORDER_AMOUNT:
      return true;
    case FIELD_KEYS.ACCOUNT_ID_NAME:
      // 海外必填，国内非必填
      return !!ctx?.overseas;
    case FIELD_KEYS.CHANNEL:
    case FIELD_KEYS.PLATFORM:
      // 显示时必填单选；供应商发起非必填
      return ctx?.initiator_role !== 'SUPPLIER';
    case FIELD_KEYS.OPTIMIZER_IDS:
      // 外包端供应商国内必填多选（仅外包端供应商显示）
      return ctx?.isExternal === true && ctx?.initiator_role === 'SUPPLIER' && !ctx?.overseas;
    case FIELD_KEYS.OVER_CHARGE_REASON:
      return calc?.need_over_charge_reason === true;
    case FIELD_KEYS.EARLY_RECHARGE_REASON:
      return calc?.need_early_recharge_reason === true;
    default:
      return false;
  }
}

/** string[] → SFormItem select options（用 name 字段，非 label） */
export function toStringOptions(list?: string[]): Array<{ value: string; name: string }> {
  return (list || []).map((v) => ({ value: v, name: v }));
}

/** 附件原因字段校验器：text 或 files 至少一项非空 */
export function reasonFieldValidator(message: string) {
  return {
    validator: (_rule: any, value?: { text?: string; files?: unknown[] }) => {
      const hasText = value?.text && String(value.text).trim();
      const hasFiles = value?.files?.length;
      return hasText || hasFiles
        ? Promise.resolve()
        : Promise.reject(new Error(message));
    },
  };
}
